import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Observable, Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  switchMap
} from 'rxjs/operators';
import { NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { EnterpriseSearchService } from '@sabstravtech/obtservices/angular';

import { ChatWidgetInputBase } from '../chat-widget-input-base';

export interface ChatFlightDetailsAnswer {
  flightCarrier: string;
  flightNumber: string;
}

interface ChatFlightCarrierChoice {
  title: string;
  value: string;
}

@Component({
  selector: 'app-chat-flight-details-input',
  standalone: false,
  templateUrl: './chat-flight-details-input.component.html',
  styleUrls: ['./chat-flight-details-input.component.scss']
})
export class ChatFlightDetailsInputComponent extends ChatWidgetInputBase implements OnChanges {
  @Input() savedValue: Record<string, unknown> | null = null;
  @Input() required = false;
  @Output() answer = new EventEmitter<ChatFlightDetailsAnswer>();
  @Output() skip = new EventEmitter<void>();

  flightCarrier = '';
  flightNumber = '';
  carrierModel: ChatFlightCarrierChoice | string | null = null;
  selectedCarrier: ChatFlightCarrierChoice | null = null;
  focus$ = new Subject<string>();
  chosenSummary: string | null = null;

  constructor(private searchService: EnterpriseSearchService) {
    super();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.onlyLockedChanged(changes)) {
      return;
    }
    this.flightCarrier = '';
    this.carrierModel = null;
    this.selectedCarrier = null;
    const savedCarrier = this.savedValue?.['flightCarrier'];
    if (typeof savedCarrier === 'string' && savedCarrier.trim()) {
      const code = savedCarrier.trim().toUpperCase();
      this.flightCarrier = code;
      this.selectedCarrier = { title: code, value: code };
      this.carrierModel = this.selectedCarrier;
      this.searchService
        .getAirlinesList(code)
        .pipe(catchError(() => of([])))
        .subscribe((airlines) => {
          const match = (airlines ?? []).find((airline) => airline.code === code);
          if (match && this.flightCarrier === code) {
            this.selectedCarrier = { title: match.name, value: match.code };
            this.carrierModel = this.selectedCarrier;
            this.flightCarrier = match.code;
          }
        });
    }
    this.flightNumber =
      this.savedValue?.['flightNumber'] != null ? String(this.savedValue['flightNumber']) : '';
    this.chosenSummary = null;
  }

  get isValid(): boolean {
    return !!this.selectedCarrier && this.normalizedFlightNumber !== null;
  }

  private get normalizedFlightNumber(): string | null {
    const raw = this.flightNumber.trim().toUpperCase().replace(/\s+/g, '');
    if (!/^\d+$/.test(raw)) {
      return null;
    }
    const carrierCode = this.selectedCarrier?.value.toUpperCase();
    return carrierCode && raw.startsWith(carrierCode) && raw.length > carrierCode.length
      ? raw.slice(carrierCode.length)
      : raw;
  }

  formatterCarriers = (choice: ChatFlightCarrierChoice): string =>
    `${choice.title} (${choice.value})`;

  carriers = (text$: Observable<string>): Observable<ChatFlightCarrierChoice[]> =>
    text$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      merge(this.focus$),
      switchMap((term: string): Observable<ChatFlightCarrierChoice[]> => {
        if (!term || term.length <= 2) {
          return of([]);
        }
        return this.searchService.getAirlinesList(term).pipe(
          map((airlines) =>
            (airlines ?? [])
              .filter((airline) => !!airline?.code && !!airline?.name)
              .map((airline) => ({ title: airline.name, value: airline.code }))
              .slice(0, 20)
          ),
          catchError(() => of([]))
        );
      })
    );

  onCarrierSelected(event: NgbTypeaheadSelectItemEvent<ChatFlightCarrierChoice>): void {
    this.selectedCarrier = event.item;
    this.flightCarrier = event.item.value;
  }

  onFlightNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const numericValue = input.value.replace(/\D/g, '');
    this.flightNumber = numericValue;
    input.value = numericValue;
  }

  onCarrierModelChange(value: ChatFlightCarrierChoice | string | null): void {
    if (value && typeof value !== 'string') {
      this.selectedCarrier = value;
      this.flightCarrier = value.value;
      return;
    }

    const carrierCode = typeof value === 'string' ? value.trim().toUpperCase() : '';
    if (!this.selectedCarrier || carrierCode !== this.selectedCarrier.value) {
      this.selectedCarrier = null;
    }
    this.flightCarrier = carrierCode;
  }

  onContinue(): void {
    if (this.locked || !this.isValid) {
      return;
    }
    const flightNumber = this.normalizedFlightNumber!;
    this.flightNumber = flightNumber;
    this.chosenSummary = `${this.selectedCarrier?.title ?? this.flightCarrier} ${flightNumber}`;
    this.answer.emit({
      flightCarrier: this.selectedCarrier!.value,
      flightNumber
    });
  }

  onSkip(): void {
    if (this.locked || this.required) {
      return;
    }
    this.chosenSummary = 'Skipped';
    this.skip.emit();
  }
}
